import { Button } from "@/components/ui/button";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { useForm } from "@tanstack/react-form";
import { useEffect } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { authApi } from "../api/auth.api";

const emailSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

interface ForgotPasswordEmailFormProps {
  emailFromUrl: string | null;
  onDone: (email: string) => void;
}

export function ForgotPasswordEmailForm({
  emailFromUrl,
  onDone,
}: ForgotPasswordEmailFormProps) {
  const form = useForm({
    defaultValues: {
      email: emailFromUrl ?? "",
    },
    validators: {
      onSubmit: emailSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        await authApi.forgotPassword(value.email);
        onDone(value.email);
      } catch (error) {
        console.error(error);
        toast.error(
          "We tried to send you an email to change your password, but something went wrong"
        );
      }
    },
  });

  useEffect(() => {
    if (emailFromUrl && !form.state.values.email) {
      form.setFieldValue("email", emailFromUrl);
    }
  }, [emailFromUrl, form]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
    >
      <FieldGroup>
        <form.Field
          name="email"
          children={(field) => {
            const isInvalid =
              field.state.meta.isTouched && !field.state.meta.isValid;

            return (
              <Field data-invalid={isInvalid}>
                <FieldLabel htmlFor={field.name}>Email</FieldLabel>
                <Input
                  id={field.name}
                  name={field.name}
                  type="email"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  aria-invalid={isInvalid}
                  placeholder="your@email.com"
                />
                <FieldError errors={field.state.meta.errors} />
              </Field>
            );
          }}
        />

        <Field>
          <Button
            type="submit"
            disabled={form.state.isSubmitting}
            className="w-full"
          >
            {form.state.isSubmitting ? "Sending..." : "Send code"}
          </Button>
        </Field>
      </FieldGroup>
    </form>
  );
}
